import {
  useEffect, useCallback, useState, useMemo, useRef
} from 'react'
import Link from 'next/link'
import BoxProduct, { ProductSkeleton } from '@/components/box-product/BoxProduct'
import NoData from '@/components/general/NoData/NoData'
import useInfiniteLoad from '@/hooks/infiniteLoad'
import styles from '@/styles/dealers.module.scss'
import { addCommas } from '@/utils/formatter'
import { useRouter } from 'next/router'
import { getMemberDetail } from '@/service/user.api'
import getMemberBadge from '@/helpers/memberBadge'
import { countUserProducts, getUserProducts } from '~/service/dealer.api'

export async function getServerSideProps(context) {
  const { id } = context.params
  if (id) {
    try {
      const response = await getMemberDetail(id)
      const { user } = response.data
      return {
        props: {
          username: user?.username || '',
          phone: user?.phone || '',
          lineId: user?.lineId || '',
          facebook: user?.facebook || '',
          profileImage: user?.profileImage?.url || '',
          isVerified: user?.isVerified,
          isPremiumMember: user?.isPremiumMember
        }
      }
    } catch (e) {
      console.log(e)
    }
  }
  return {
    redirect: {
      destination: '/',
      permanent: false
    }
  }
}

export default function DealerProducts({
  username, phone, lineId, facebook, profileImage, isPremiumMember, isVerified
}) {
  const limit = 12
  const [currentPage, setCurrentPage] = useState(1)
  const router = useRouter()
  const productListRef = useRef()
  const [loading, setLoading] = useState(true)
  const { id } = router.query
  const [countAll, setCountAll] = useState(0)
  const [productList, setProductList] = useState([])
  const currentOffset = useMemo(() => (currentPage - 1) * limit, [currentPage])
  const maxPage = useMemo(() => Math.ceil(countAll / limit), [countAll])

  const fetchBrandProduct = useCallback(async () => {
    if (id) {
      try {
        setLoading(true)
        const response = await getUserProducts(id, currentOffset, limit)
        setProductList((prev) => ([...prev, ...response.data.userProducts]))
      } catch (error) {
        // console.log(error)
      } finally {
        setLoading(false)
      }
    }
  }, [id, currentOffset])

  const fetchBrandProductCount = useCallback(async () => {
    if (id) {
      try {
        const response = await countUserProducts(id)
        // console.log(alert(JSON.stringify(response)))
        setCountAll(response.data.userProductsCount)
      } catch (error) {
        // alert(error)
      }
    }
  }, [id])

  useEffect(() => {
    fetchBrandProductCount()
  }, [fetchBrandProductCount])

  useEffect(() => {
    fetchBrandProduct()
  }, [fetchBrandProduct])

  const loadMore = () => {
    if (currentPage < maxPage) {
      setCurrentPage((prev) => prev + 1)
    }
  }

  useInfiniteLoad(productListRef, loadMore)

  const badgeData = getMemberBadge({
    isVerified,
    isPremiumMember
  })
  const badge = (
    <div
      className={styles.status}
      {
          ...(
            badgeData.color && { style: { '--badge-color': badgeData.color } }
          )
        }
    >
      {badgeData.text}
    </div>
  )

  return (
    <div className={styles.dealer}>
      <section className={styles.dealerDetail}>
        <div className="max-width">
          <h1>Dealer Details</h1>
          <div className={styles.row}>
            <div className={styles.profile}>
              <div className={styles.imgWrap}>
                <img className={styles.avatar} src={profileImage || '/icon/default-avatar.png'} />
              </div>
            </div>
            <div className={styles.detail}>
              <div className={styles.name}>{username}</div>
              {badge}
              <div className={styles.contactList}>
                <div className={styles.contact}>
                  <span className={styles.key}>Phone:</span>
                  {
                    phone
                    ? (
                      <Link href={`tel:${phone}`}>
                        <a href={`tel:${phone}`}>
                          {phone || '-'}
                        </a>
                      </Link>
                    ) : '-'
                  }
                </div>
                <div className={styles.contact}>
                  <span className={styles.key}>Facebook:</span>
                  {
                    facebook && facebook !== 'null'
                    ? (
                      <Link href={`${facebook}`}>
                        <a href={`${facebook}`} rel="noreferer noreferrer" target="_blank" className={styles.link}>
                          {facebook || '-'}
                        </a>
                      </Link>
                    ) : '-'
                  }
                </div>
                <div className={styles.contact}>
                  <span className={styles.key}>Line Id:</span>
                  {
                    lineId
                    ? (
                      <Link href={`https://line.me/ti/p/~${lineId}`}>
                        <a href={`https://line.me/ti/p/~${lineId}`}>
                          {lineId || '-'}
                        </a>
                      </Link>
                    ) : '-'
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="max-width">
        <section className={styles.productList} ref={productListRef}>
          {
            !productList.length && !loading
              ? (
                <NoData />
              )
              : (
                <>
                  {
                    productList.map((item) => (
                      <BoxProduct
                        key={item.id}
                        url={`/product/${item.id}`}
                        image={item.images?.[0]?.url || ''}
                        title={item.name}
                        price={`฿ ${addCommas(item.price?.toString() || '0')}`}
                        subTitle={item.model}
                        category={item.brand?.brandName || 'Others'}
                      />
                    ))
                  }
                  {
                    loading && <ProductSkeleton />
                  }
                </>
              )
          }
        </section>
      </div>
    </div>
  )
}
